/**
 * Modelo del dominio Proyecto, alineado al backend
 * (`prisma/schema.prisma`, `ProyectoResponseDto`).
 *
 * - `id` y FKs son `number` (Prisma autoincrement).
 * - `estado` y `fase` son enums string PascalCase.
 * - `activo` distingue el estado administrativo (soft-delete) del
 *   estado de negocio (`estado`).
 */

import type { CategoriaControl } from './categoria-control.entity';
import type { ProyectoFase } from './proyecto-arbol.entity';
import type { EstadoProyecto, FaseProyecto } from './proyecto-tipos';

export type { EstadoProyecto, FaseProyecto } from './proyecto-tipos';

export const ESTADOS_PROYECTO: ReadonlyArray<EstadoProyecto> = [
  'Borrador',
  'PendienteInicio',
  'Activo',
  'EnIncumplimiento',
  'Suspendido',
  'Cerrado',
];

export const FASES_PROYECTO: ReadonlyArray<FaseProyecto> = ['Construccion', 'Explotacion'];

export const ESTADO_PROYECTO_LABEL: Record<EstadoProyecto, string> = {
  Borrador: 'Borrador',
  PendienteInicio: 'Pendiente de inicio',
  Activo: 'Activo',
  EnIncumplimiento: 'En incumplimiento',
  Suspendido: 'Suspendido',
  Cerrado: 'Cerrado',
};

export const FASE_PROYECTO_LABEL: Record<FaseProyecto, string> = {
  Construccion: 'Construcción',
  Explotacion: 'Explotación',
};

export function estadoProyectoLabel(e: EstadoProyecto): string {
  return ESTADO_PROYECTO_LABEL[e];
}

export function faseProyectoLabel(f: FaseProyecto | null): string {
  return f ? FASE_PROYECTO_LABEL[f] : 'Sin fase';
}

/**
 * Tono canónico por estado — fuente única usada por el `EstadoProyectoBadge`
 * y por las tarjetas de totales del listado. La capa de UI traduce cada tono
 * al `variant` correspondiente del Badge / IconBadge.
 *
 *  - Borrador        → neutral (gris, "en construcción, aún no finalizado")
 *  - PendienteInicio → info (azul tenue, "esperando arranque")
 *  - Activo          → success (verde, "en operación")
 *  - EnIncumplimiento → warning (naranja, "alerta")
 *  - Suspendido      → danger (rojo, "bloqueado")
 *  - Cerrado         → neutral (gris, "archivado")
 */
export type EstadoProyectoTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export const ESTADO_PROYECTO_TONE: Record<EstadoProyecto, EstadoProyectoTone> = {
  Borrador: 'neutral',
  PendienteInicio: 'info',
  Activo: 'success',
  EnIncumplimiento: 'warning',
  Suspendido: 'danger',
  Cerrado: 'neutral',
};

export function estadoProyectoTone(e: EstadoProyecto): EstadoProyectoTone {
  return ESTADO_PROYECTO_TONE[e];
}

/**
 * Regla de negocio: un proyecto **solo es editable mientras no ha
 * comenzado**. Debe estar activo (no soft-deleted) y en estado
 * `PendienteInicio`. Una vez iniciado (`Activo`/`EnIncumplimiento`/
 * `Suspendido`/`Cerrado`), sus datos generales quedan congelados para
 * preservar la trazabilidad del arranque.
 *
 * Fuente única consumida por el CTA "Editar proyecto" (gating local) y
 * por la guarda de la página de edición (acceso directo por URL).
 */
export function proyectoEsEditable(p: Pick<Proyecto, 'estado' | 'activo'>): boolean {
  return p.activo && p.estado === 'PendienteInicio';
}

/**
 * Regla de negocio: un proyecto **ya iniciado** (`Activo`, `EnIncumplimiento`
 * o `Suspendido`) ya no admite edición total —su estructura está congelada para
 * preservar la trazabilidad del arranque—, pero **sí admite ampliarse**: se le
 * pueden agregar fases, subfases, condiciones mínimas e indicadores nuevos, y
 * sumar miembros al equipo, sin tocar nada de lo existente. `Cerrado` queda
 * fuera (requiere reabrirse primero) y `Borrador` se configura por `/continuar`.
 *
 * Fuente única consumida por el CTA "Ampliar proyecto" (gating local) y por la
 * guarda de la página de edición (acceso directo por URL).
 */
export function proyectoPermiteAmpliar(p: Pick<Proyecto, 'estado' | 'activo'>): boolean {
  return (
    p.activo &&
    (p.estado === 'Activo' || p.estado === 'EnIncumplimiento' || p.estado === 'Suspendido')
  );
}

/**
 * Modo de edición aplicable a un proyecto, fuente única para el CTA del detalle
 * y la guarda de `/proyectos/:id/editar`:
 *  - `'editar'`  → edición total (proyecto aún sin iniciar, `PendienteInicio`).
 *  - `'ampliar'` → solo agregar (proyecto ya iniciado, ver `proyectoPermiteAmpliar`).
 *  - `null`      → no editable aquí (`Borrador` se retoma en `/continuar`; `Cerrado`
 *                  requiere reabrirse).
 */
export type ProyectoModoEdicion = 'editar' | 'ampliar';

export function proyectoModoEdicion(
  p: Pick<Proyecto, 'estado' | 'activo'>,
): ProyectoModoEdicion | null {
  if (proyectoEsEditable(p)) return 'editar';
  if (proyectoPermiteAmpliar(p)) return 'ampliar';
  return null;
}

/**
 * Regla de negocio: un proyecto en `Borrador` está a medio crear (wizard por
 * etapas, ADR-034) y **todavía no tiene detalle navegable** (ni vista clásica
 * `/proyectos/:id` ni tablero de cumplimiento). Su única continuación es retomar
 * el wizard. Solo los proyectos que ya finalizaron su creación
 * (`PendienteInicio` en adelante) exponen detalle.
 *
 * Fuente única consumida por el listado (enruta los borradores a `/continuar`) y
 * por la guarda de las vistas de detalle/cumplimiento (acceso directo por URL).
 */
export function proyectoEsBorrador(p: Pick<Proyecto, 'estado'>): boolean {
  return p.estado === 'Borrador';
}

/**
 * Regla de negocio: las acciones operativas sobre el proyecto (marcar
 * condiciones/indicadores como cumplidos, reportar incidencias) **solo se
 * permiten mientras el proyecto está en operación normal**: activo (no
 * soft-deleted) y en estado `Activo`. En cualquier otro estado
 * (`PendienteInicio`, `EnIncumplimiento`, `Suspendido`, `Cerrado`) la
 * operación queda bloqueada.
 *
 * Fuente única consumida por los CTAs operativos del detalle para
 * deshabilitarse de forma coherente.
 */
export function proyectoPermiteOperar(p: Pick<Proyecto, 'estado' | 'activo'>): boolean {
  return p.activo && p.estado === 'Activo';
}

/**
 * Regla de negocio: un proyecto está "finalizado" cuando alcanzó su estado
 * terminal `Cerrado`. En ese punto su estructura queda congelada: no se
 * agregan/quitan integrantes del equipo ni se suben/eliminan archivos KMZ
 * (solo lectura/descarga).
 *
 * **ADR-034 (cumplimiento invertido):** el `%` ya NO es señal de finalización.
 * El proyecto nace al 100% (sano, sin incidencias) y baja con ellas, así que un
 * `PendienteInicio`/`Activo` al 100% NO está finalizado. El auto-cierre
 * (ADR-027) ya transiciona a `Cerrado` cuando se completa la última subfase, por
 * lo que `estado === 'Cerrado'` es la única señal correcta y suficiente.
 */
export function proyectoEstaFinalizado(p: Pick<Proyecto, 'estado'>): boolean {
  return p.estado === 'Cerrado';
}

export interface CatalogoMinimal {
  id: number;
  nombre: string;
  activo: boolean;
}

export interface UsuarioMinimal {
  id: number;
  nombre: string;
  nombreCompleto: string | null;
  email: string;
}

export interface ProyectoSupervisorEmbedded {
  usuario: UsuarioMinimal;
  asignadoPor: UsuarioMinimal | null;
  /** ISO 8601 UTC. */
  asignadoEn: string;
}

/**
 * Espejo de `ProyectoAuxiliarEmbeddedDto`. Misma forma que
 * `ProyectoSupervisorEmbedded` pero conserva el alias para que las
 * vistas que listan auxiliares puedan tipar el embed sin importar el
 * tipo de supervisor.
 */
export interface ProyectoAuxiliarEmbedded {
  usuario: UsuarioMinimal;
  asignadoPor: UsuarioMinimal | null;
  /** ISO 8601 UTC. */
  asignadoEn: string;
}

/**
 * Item del listado paginado (`GET /api/v1/proyectos`). Espeja
 * `ProyectoListItemResponseDto` del backend: cabecera + FKs +
 * catálogos populated + autoría populated + supervisor (cardinalidad
 * 1) + auxiliares (cardinalidad N) embebidos + el conteo de participantes
 * privados (`participantesPrivadosTotal`). **No** incluye el árbol de fases
 * ni el detalle de los participantes privados — eso lo trae el detalle / el
 * sub-recurso `GET /proyectos/:id/participantes-privados`.
 *
 * Campos legacy (`fase`, `creadoPorId`, `actualizadoPorId`) quedan
 * opcionales para no romper la vista de detalle/edit hasta el rework
 * mayor del detalle.
 */
export interface Proyecto {
  id: number;
  nombre: string;
  /**
   * Siglas del código oficial de incidencias (`RI-SUP-<SIGLAS>-<N>-<YYYY>`).
   * `null` = sin configurar (el backend usa el fallback `P<id>`). Opcional
   * porque el listado (`ProyectoListItemResponseDto`) no lo incluye; el
   * detalle y las respuestas de create/update sí.
   */
  siglas?: string | null;
  descripcion: string | null;
  estado: EstadoProyecto;
  fechaInicio: string;
  fechaFinEstimada: string;
  origenProyectoId: number;
  naturalezaFinancieraId: number;
  tipoInfraestructuraId: number;
  tipoServicioId: number;
  activo: boolean;
  origenProyecto: CatalogoMinimal | null;
  naturalezaFinanciera: CatalogoMinimal | null;
  tipoInfraestructura: CatalogoMinimal | null;
  tipoServicio: CatalogoMinimal | null;
  creadoPor: UsuarioMinimal | null;
  creadoEn: string;
  actualizadoPor: UsuarioMinimal | null;
  actualizadoEn: string;
  iniciadoPor: UsuarioMinimal | null;
  iniciadoEn: string | null;
  supervisor: ProyectoSupervisorEmbedded | null;
  auxiliares: ProyectoAuxiliarEmbedded[];
  /**
   * Conteo de participantes privados asignados (líder + auxiliares de
   * campo). El listado solo trae el total; el detalle de cada uno se
   * obtiene de `GET /proyectos/:id/participantes-privados`.
   */
  participantesPrivadosTotal: number;
  /**
   * Conteo de solicitantes asignados (actor externo de solo lectura). El
   * listado solo trae el total; el detalle de cada uno se obtiene de
   * `GET /proyectos/:id/solicitantes`.
   */
  solicitantesTotal: number;

  /**
   * Categorías de control del proyecto (ADR-032b): agrupan las condiciones e
   * indicadores por cuerpo legal (`tipoControl`) y eje (`Operación`/
   * `Mantenimiento`). Solo viene en el detalle; el listado no lo incluye.
   */
  categorias?: CategoriaControl[];

  /**
   * Arbol estructural devuelto por `GET /api/v1/proyectos/:id`
   * (`ProyectoCompletoResponseDto`). Las fases son **dinámicas** (ADR-032): 1,
   * 2 o N, cada una con `nombre`/`orden`/`estado`. El listado paginado
   * (`GET /api/v1/proyectos`) NO lo incluye — de ahi que sea opcional.
   */
  fases?: ProyectoFase[];

  // Campos legacy — opcionales para preservar contratos previos hasta
  // que la vista de detalle adopte `fases[]` directamente.
  fase?: FaseProyecto | null;
  creadoPorId?: number;
  actualizadoPorId?: number | null;
}
